import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { HistoryEntry } from '../state/historyStore';
import { CropWorkspace } from './cropper/CropWorkspace';

interface ComparisonViewerProps {
  entry: HistoryEntry;
  onSelectOriginal?: (entry: HistoryEntry) => void | Promise<void>;
}

export function ComparisonViewer({ entry, onSelectOriginal }: ComparisonViewerProps) {
  const { t, i18n } = useTranslation();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState<'view' | 'crop'>('view');
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const bodyOverflowRef = useRef<string>('');
  const [toolbarHost, setToolbarHost] = useState<HTMLDivElement | null>(null);
  const toolbarHostRef = useCallback((node: HTMLDivElement | null) => {
    setToolbarHost(node);
  }, []);

  // Detect if device is mobile
  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }, []);

  const formatTimestamp = useCallback(
    (value?: string) => {
      const source = value ?? entry.createdAt;
      const date = new Date(source);
      if (Number.isNaN(date.getTime())) {
        return undefined;
      }
      const pad = (num: number) => String(num).padStart(2, '0');
      return [
        date.getFullYear(),
        pad(date.getMonth() + 1),
        pad(date.getDate()),
        pad(date.getHours()),
        pad(date.getMinutes()),
        pad(date.getSeconds())
      ].join('-');
    },
    [entry.createdAt]
  );

  // Direct download (for desktop download button)
  const handleDownload = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const response = await fetch(entry.result.dataUrl);
      const blob = await response.blob();
      const extension = blob.type.split('/')[1] || 'png';
      const timestamp =
        formatTimestamp(entry.result.completedAt) ??
        formatTimestamp(entry.createdAt) ??
        entry.id;
      const safeStyleName =
        entry.style.name.trim().replace(/\s+/g, '-') || entry.style.id || 'styled-image';
      const filename = `${safeStyleName}-${timestamp}.${extension}`;

      // Direct download without Web Share API
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 500);
    } catch (error) {
      console.error('Failed to download result image', error);
    }
  }, [entry.createdAt, entry.id, entry.result.completedAt, entry.result.dataUrl, entry.style.id, entry.style.name, formatTimestamp]);

  // Share via Web Share API (for desktop share button and mobile)
  const handleShare = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const response = await fetch(entry.result.dataUrl);
      const blob = await response.blob();
      const extension = blob.type.split('/')[1] || 'png';
      const timestamp =
        formatTimestamp(entry.result.completedAt) ??
        formatTimestamp(entry.createdAt) ??
        entry.id;
      const safeStyleName =
        entry.style.name.trim().replace(/\s+/g, '-') || entry.style.id || 'styled-image';
      const filename = `${safeStyleName}-${timestamp}.${extension}`;

      // Try using the Web Share API
      if (navigator.share && navigator.canShare) {
        const mimeType = blob.type || 'image/png';
        const file = new File([blob], filename, { type: mimeType });
        const shareData = { files: [file] };

        if (navigator.canShare(shareData)) {
          try {
            await navigator.share(shareData);
            return;
          } catch (shareError) {
            // Ignore AbortError (user cancelled share sheet)
            if ((shareError as Error).name !== 'AbortError') {
              console.warn('Share failed, falling back to download', shareError);
            } else {
              return;
            }
          }
        }
      }

      // Fallback to download if share is not available
      await handleDownload();
    } catch (error) {
      console.error('Failed to share result image', error);
    }
  }, [entry.createdAt, entry.id, entry.result.completedAt, entry.result.dataUrl, entry.style.id, entry.style.name, formatTimestamp, handleDownload]);

  const openPreview = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setPreviewOpen(true);
    setPreviewMode('view');
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    setPreviewMode('view');
  }, []);

  useEffect(() => {
    if (!previewOpen || typeof window === 'undefined') {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePreview();
      }
    };

    const body = document.body;
    bodyOverflowRef.current = body.style.overflow;
    body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    requestAnimationFrame(() => {
      dialogRef.current?.focus({ preventScroll: true });
    });

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      body.style.overflow = bodyOverflowRef.current;
      bodyOverflowRef.current = '';
      previousFocusRef.current?.focus({ preventScroll: true });
    };
  }, [closePreview, previewOpen]);

  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 0
      }),
    [i18n.language]
  );

  const previewTitle = useMemo(
    () => t('history.preview.dialogTitle', { style: entry.style.name }),
    [entry.style.name, t]
  );
  const reuseOriginalLabel = useMemo(
    () => t('history.actions.useOriginal'),
    [t]
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-ai-surface-glow/30 bg-ai-surface-elevated/60 p-4">
      <div className="grid grid-cols-2 gap-3">
        <figure className="relative">
          <img
            src={entry.original.dataUrl}
            alt={t('history.compare.original')}
            className="h-full w-full rounded-xl object-cover"
          />
          {onSelectOriginal ? (
            <button
              type="button"
              className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/0 text-white transition-[background-color] hover:bg-black/20 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
              onClick={() => {
                void onSelectOriginal(entry);
              }}
              aria-label={reuseOriginalLabel}
              title={reuseOriginalLabel}
            >
              <span className="sr-only">{reuseOriginalLabel}</span>
            </button>
          ) : null}
        </figure>
        <figure className="relative">
          <img
            src={entry.result.dataUrl}
            alt={t('history.compare.styled')}
            className="h-full w-full rounded-xl object-cover"
          />
          <button
            type="button"
            className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/0 text-white transition-[background-color] hover:bg-black/20 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
            onClick={openPreview}
            aria-label={t('history.preview.openLabel')}
            title={t('history.preview.openLabel')}
          >
            <span className="sr-only">{t('history.preview.openLabel')}</span>
          </button>
          {isMobile ? (
            // Mobile: Only show share button
            <button
              type="button"
              className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full border border-ai-surface-glow/30 bg-ai-surface-elevated/80 text-ai-text-primary shadow-sm transition-colors hover:border-ai-primary/40 hover:text-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
              aria-label={t('history.actions.downloadResult')}
              title={t('history.actions.downloadResult')}
              onClick={handleShare}
            >
              <svg
                aria-hidden="true"
                className="h-5 w-5"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 3v12m0 0l-4-4m4 4l4-4M6 19h12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : (
            // Desktop: Show both download and share buttons
            <>
              <button
                type="button"
                className="absolute bottom-3 right-14 flex h-10 w-10 items-center justify-center rounded-full border border-ai-surface-glow/30 bg-ai-surface-elevated/80 text-ai-text-primary shadow-sm transition-colors hover:border-ai-primary/40 hover:text-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                aria-label={t('history.actions.shareResult')}
                title={t('history.actions.shareResult')}
                onClick={handleShare}
              >
                <svg
                  aria-hidden="true"
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full border border-ai-surface-glow/30 bg-ai-surface-elevated/80 text-ai-text-primary shadow-sm transition-colors hover:border-ai-primary/40 hover:text-ai-primary focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                aria-label={t('history.actions.downloadResult')}
                title={t('history.actions.downloadResult')}
                onClick={handleDownload}
              >
                <svg
                  aria-hidden="true"
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 3v12m0 0l-4-4m4 4l4-4M6 19h12"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </>
          )}
        </figure>
      </div>
      {previewOpen && typeof document !== 'undefined'
        ? createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
            role="dialog"
            aria-modal="true"
            aria-label={previewTitle}
            onClick={closePreview}
          >
            <div
              className="relative w-full max-w-5xl rounded-3xl bg-ai-surface shadow-ai-card"
              onClick={(event) => event.stopPropagation()}
              ref={dialogRef}
              tabIndex={-1}
            >
              <div className="flex items-start justify-between gap-3 border-b border-ai-surface-glow/20 px-6 py-4">
                <div>
                  <h3 className="font-display text-xl font-semibold text-ai-text-primary">
                    {previewMode === 'crop' ? t('history.crop.heading') : t('history.preview.dialogTitle', { style: entry.style.name })}
                  </h3>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {previewMode === 'crop' ? (
                    <div
                      ref={toolbarHostRef}
                      className="flex min-h-[40px] flex-wrap items-center gap-2"
                    />
                  ) : (
                    <button
                      type="button"
                      className="rounded-full border border-ai-primary/40 px-3 py-1 text-sm font-semibold text-ai-primary transition hover:bg-ai-primary/10 focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                      onClick={() => setPreviewMode('crop')}
                    >
                      {t('history.crop.actions.open')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-ai-surface-glow/40 bg-ai-surface-elevated/80 text-ai-text-primary shadow-ai-card transition hover:bg-ai-primary/80 hover:text-white focus:outline-none focus-visible:ring focus-visible:ring-ai-focus"
                    onClick={closePreview}
                    aria-label={t('history.preview.close')}
                  >
                    <span aria-hidden="true" className="text-xl font-semibold">
                      ×
                    </span>
                  </button>
                </div>
              </div>
              <div className="max-h-[80vh] w-full overflow-visible rounded-b-3xl">
                {previewMode === 'crop' ? (
                  <div className="h-[70vh]">
                    <CropWorkspace
                      entry={entry}
                      onClose={() => setPreviewMode('view')}
                      variant="embedded"
                      toolbarPlacement="portal"
                      toolbarPortalTarget={toolbarHost}
                    />
                  </div>
                ) : (
                  <>
                    <img
                      src={entry.result.dataUrl}
                      alt={t('history.compare.styled')}
                      className="max-h-[70vh] w-full object-contain"
                    />
                    <div className="px-6 pb-6 text-right text-xs text-ai-text-secondary">
                      <span>
                        {numberFormatter.format(entry.result.width)} × {numberFormatter.format(entry.result.height)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body
        )
        : null}
    </div>
  );
}
